import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { jsx } from '@emotion/react';
import Flag, { FlagGroup, AutoDismissFlag } from '@atlaskit/flag';
import Tick from '@atlaskit/icon/glyph/check-circle';
import Error from '@atlaskit/icon/glyph/error';
import Info from '@atlaskit/icon/glyph/info';
import Warning from '@atlaskit/icon/glyph/warning';
import { v4 as uuidv4 } from 'uuid';
import {
  ShowFn,
  AppearanceTypes,
  Callback,
  Id, noop, Notifications,
  Notification,
  Options,
  RemoveFn,
  UpdateFn,
} from './types';
import { colors } from '../theme';
// import AutoDismissFlag from './AutoDismissFlag';

type State = { notifications: Notifications };

type Action = {
  type: 'ADD' | 'REMOVE' | 'REMOVE_ALL' | 'UPDATE';
  payload: any;
}

type Context = {
  show: ShowFn;
  hide: RemoveFn;
  hideAll: () => void;
  update: UpdateFn;
  // notifications: Notifications;
};

const iconMap = (key: AppearanceTypes, color?: string) => {
  const icons: { [key: string]: React.ReactElement } = {
    info: <Info label="Info icon" primaryColor={color || colors.P300} />,
    success: <Tick label="Success icon" primaryColor={color || colors.G300} />,
    warning: (
      <Warning label="Warning icon" primaryColor={color || colors.Y300} />
    ),
    error: <Error label="Error icon" primaryColor={color || colors.R300} />,
    normal: <Info label="Normal info icon" primaryColor={color || colors.P300} />,
  };

  return icons[key] || icons.success;
};


export const NotificationContext = React.createContext<Context | null>(null);
const { Consumer, Provider } = NotificationContext;

export const NotificationProvider: React.FC = ({ children }) => {

  const [notifications, setNotifications] = useState<Notifications>([]);

  const show = useCallback((title: React.ReactNode, description, options: Options = { }) => {
    const id =  options.id || uuidv4();
    const notification:Notification = {
      type: 'info',
      autoDismiss: true,
      duration: 5000,
      ...options,
      id,
      title,
      description,
    };
    setNotifications([...notifications, notification]);
    return id;
  },[]);

  const hide = useCallback((id: Id) => {
    setNotifications(notifications.filter(notification => notification.id !== id));
  },[notifications]);

  const hideAll = useCallback(() => {
    setNotifications([]);
  },[]);

  const update = useCallback((id: Id, options: Options) => {
    alert('Not implemented yet');
  },[]);

  const handleOnDismiss = useCallback((id: Id, cb: Callback = noop) => () => {
      cb(id);
      hide(id);
    }
  ,[hide]);

  const value = useMemo(() => ({
    show,
    hide,
    hideAll,
    update,
    // notifications,

  }),[]);

  return (
    <NotificationContext.Provider
      value={value}
    >
      {children}
      <FlagGroup onDismissed={hide}>
        {
          notifications.map(({
                              id,
                              type,
                              title,
                              description,
                              autoDismiss,
                              onDismiss,
                              duration,
                                ...unknownConsumerProps
                              }) => {
            const FlagComponent = autoDismiss ? AutoDismissFlag : Flag;
            return (
            <FlagComponent
              icon={iconMap(type)}
              id={id}
              key={id}
              title={title}
              onDismissed={() => handleOnDismiss(id, onDismiss)}
              description={description}
              // duration={duration}
              appearance={type ==='error' ? 'error' : 'normal'}
            />
          )
          })
        }


      </FlagGroup>
    </NotificationContext.Provider>
  );
};

